import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { playDing } from "@/lib/audio";
import { CheckCircle, XCircle, HourglassMedium, Files, ArrowSquareOut, ChartBar, Warning, ArrowRight } from "@phosphor-icons/react";

const fmtInr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtCompact = (n) => {
    const v = Number(n || 0);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
    if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
    return `₹${v.toFixed(0)}`;
};

/**
 * v1.27.80 — Compact Monthly Slip Summary tile for the Admin Dashboard.
 * Shows only heading + 4 status counters. Full breakdown, trend chart,
 * per-company table, Missing UTR and Pending Uploads sections all live
 * on the dedicated /admin/reports/slip-summary page. Tile blinks
 * red/orange whenever `pending.count > 0`.
 */
export default function MonthlySlipSummary({ refreshKey = 0 }) {
    const [data, setData] = useState(null);
    const prevPendingRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        let alive = true;
        api.get("/dashboard/monthly-slip-summary")
            .then((r) => {
                if (!alive) return;
                setData(r.data);
                // v1.27.81 — Chime when pending count INCREASES (a new slip
                // just landed). On very first load prevPendingRef is null so
                // we simply prime it and stay silent.
                const nowPending = r.data?.current?.pending?.count || 0;
                if (prevPendingRef.current !== null && nowPending > prevPendingRef.current) {
                    // v1.27.81 — Use the shared WebAudio ding helper so we
                    // don't depend on a static /ding.mp3 file. Silent no-op
                    // if the browser hasn't received a user gesture yet.
                    try { playDing(); } catch {}
                }
                prevPendingRef.current = nowPending;
            })
            .catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [refreshKey]);

    if (!data) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="monthly-slip-summary-loading">
                <div className="text-xs text-slate-400">Loading Monthly Slip Summary…</div>
            </div>
        );
    }

    const c = data.current;
    const hasPending = (c?.pending?.count || 0) > 0;
    const cards = [
        { key: "accepted", label: "Accepted", icon: CheckCircle,     count: c.accepted.count, amount: c.accepted.amount, tone: "border-emerald-200 bg-emerald-50 text-emerald-900", accent: "text-emerald-700" },
        { key: "rejected", label: "Rejected", icon: XCircle,         count: c.rejected.count, amount: c.rejected.amount, tone: "border-rose-200 bg-rose-50 text-rose-900",        accent: "text-rose-700" },
        { key: "pending",  label: "Pending",  icon: HourglassMedium, count: c.pending.count,  amount: c.pending.amount,  tone: "border-amber-200 bg-amber-50 text-amber-900",     accent: "text-amber-700" },
        { key: "total",    label: "Total",    icon: Files,           count: c.total.count,    amount: c.total.amount,    tone: "border-slate-300 bg-slate-50 text-slate-900",     accent: "text-slate-700" },
        { key: "missing_utr", label: "No UTR", icon: Warning,        count: data.missing_utr?.count || 0, amount: null,     tone: "border-violet-200 bg-violet-50 text-violet-900",  accent: "text-violet-700" },
    ];

    return (
        <button
            type="button"
            onClick={() => navigate("/admin/reports/slip-summary")}
            data-testid="monthly-slip-summary-tile"
            className="group w-full rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                        <Files size={18} weight="bold" />
                        {hasPending && (
                            <>
                                <span
                                    aria-hidden
                                    data-testid="slip-summary-pending-blink"
                                    className="absolute -right-0.5 -top-0.5 inline-flex h-3 w-3 items-center justify-center"
                                >
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-75" />
                                    <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600 ring-2 ring-white" />
                                </span>
                            </>
                        )}
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Monthly Slip Summary</div>
                        <div className="font-display text-base font-bold tracking-tight text-slate-900" data-testid="monthly-summary-heading">{data.heading}</div>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 opacity-70 transition-opacity group-hover:opacity-100">
                    View Details <ArrowSquareOut size={11} weight="bold" />
                </span>
            </div>
            <div className="mt-2 grid grid-cols-5 gap-1.5">
                {cards.map(({ key, label, icon: Icon, count, amount, tone, accent }) => (
                    <div key={key} className={`rounded-lg border ${tone} p-1.5`} data-testid={`monthly-summary-${key}`}>
                        <div className="flex items-center justify-between gap-1">
                            <div className={`truncate text-[8px] font-bold uppercase tracking-[0.12em] ${accent}`}>{label}</div>
                            <Icon size={10} weight="duotone" className={`shrink-0 ${accent}`} />
                        </div>
                        <div className="mt-0.5 font-display text-base font-bold tabular-nums leading-tight" data-testid={`monthly-summary-${key}-count`}>{count}</div>
                        {amount != null && (
                            <div className="text-[9px] font-mono text-slate-500 truncate" title={fmtInr(amount)} data-testid={`monthly-summary-${key}-amount`}>{fmtCompact(amount)}</div>
                        )}
                    </div>
                ))}
            </div>
        </button>
    );
}
